// Cheque Book verification (legacy /AccountsApproval/VerifyCheque).
// Pending rows are verified/approved via the shared status list; rows returned for update
// (Approvalstatus '0') get legacy's resubmit form (bank, issue date, 6-digit cheque range, remarks).
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { BookOpen, Clock, Building2, RotateCcw, Send } from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import ActionButtons from '../../components/Inbox/ActionButtons';
import RemarksHistory from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import { SimpleCommentInput } from '../../components/Inbox/VerificationInput';

import {
    fetchVerifyChequeQueue,
    fetchVerifyChequeDetail,
    fetchReturnedChequeDetail,
    fetchChequeBanks,
    submitVerifyCheque,
    submitUpdateCheque,
    clearVerifyChequeDetail,
    resetVerifyCheque,
    selectVCQueue, selectVCQueueLoading, selectVCQueueError,
    selectVCDetail, selectVCDetailLoading, selectVCDetailError,
    selectVCBanks, selectVCVerifyLoading, selectVCUpdateLoading,
} from '../../slices/accountsSlice/verifyChequeSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

import {
    fetchRemarks,
    selectRemarks,
    selectRemarksLoading,
    setSelectedMOID,
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

const ACTION_VERB = {
    Verify: 'Verified Successfully',
    Approve: 'Approved Successfully',
    Return: 'Returned for Update',
    Reject: 'Rejected Successfully',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const Field = ({ label, value, className = '' }) => (
    <div className={className}>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 break-words">{value || value === 0 ? value : '—'}</p>
    </div>
);

const StatusBadge = ({ status }) => (String(status) === '0' ? (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 whitespace-nowrap">Return</span>
) : (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 whitespace-nowrap">Verification Pending</span>
));

const inputCls = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';
const labelCls = 'block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1';

const todayIso = () => new Date().toISOString().slice(0, 10);
// "25-Sep-2026" → "2026-09-25"
const legacyToIso = (v) => {
    const m = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/.exec(String(v || '').trim());
    if (!m) return '';
    const month = MONTHS.findIndex((x) => x.toLowerCase() === m[2].toLowerCase()) + 1;
    return month ? `${m[3]}-${String(month).padStart(2, '0')}-${m[1].padStart(2, '0')}` : '';
};
// "2026-09-25" → "25-Sep-2026" — legacy's datepicker format; the SP receives @Issuedate as a string.
const isoToLegacy = (v) => {
    const [y, mo, d] = String(v || '').split('-');
    return y && mo && d ? `${d}-${MONTHS[Number(mo) - 1]}-${y}` : '';
};

const VerifyCheque = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;

    const queue = useSelector(selectVCQueue);
    const queueLoading = useSelector(selectVCQueueLoading);
    const queueError = useSelector(selectVCQueueError);
    const detail = useSelector(selectVCDetail);
    const detailLoading = useSelector(selectVCDetailLoading);
    const detailError = useSelector(selectVCDetailError);
    const banks = useSelector(selectVCBanks);
    const verifyLoading = useSelector(selectVCVerifyLoading);
    const updateLoading = useSelector(selectVCUpdateLoading);

    const statusLoading = useSelector(selectStatusListLoading);
    const statusError = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions = useSelector(selectHasActions);

    const remarks = useSelector(selectRemarks);
    const remarksLoading = useSelector(selectRemarksLoading);

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    const [selectedItem, setSelectedItem] = useState(null);
    const [comment, setComment] = useState('');
    const [showRemarksHistory, setShowRemarksHistory] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);
    // Returned-row resubmit form
    const [upd, setUpd] = useState({ bankId: '', date: '', from: '', to: '', remarks: '' });
    const setField = (key, value) => setUpd((prev) => ({ ...prev, [key]: value }));

    const getCurrentUser = () => userData?.userName || userDetails?.userName || 'system';

    const isReturned = String(selectedItem?.Approvalstatus) === '0';
    // Only trust the detail that belongs to the row currently selected.
    const cheque = selectedItem && detail?.chequeid === selectedItem.chequeid ? detail : null;

    useEffect(() => {
        if (roleId) dispatch(fetchVerifyChequeQueue(roleId));
    }, [roleId, dispatch]);

    useEffect(() => {
        // Legacy's status dropdown on this page keeps every option, including "Return".
        dispatch(setShowReturnButton('Yes'));
        return () => {
            dispatch(resetVerifyCheque());
            dispatch(resetApprovalData());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!selectedItem) return;
        dispatch(clearVerifyChequeDetail());
        dispatch(resetApprovalData());
        dispatch(setShowReturnButton('Yes'));
        if (String(selectedItem.Approvalstatus) === '0') {
            dispatch(fetchReturnedChequeDetail(selectedItem.chequeid));
            if (!banks.length) dispatch(fetchChequeBanks());
        } else {
            dispatch(fetchVerifyChequeDetail(selectedItem.chequeid));
        }
        setComment('');
        setShowRemarksHistory(false);
        setIsLeftPanelCollapsed(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    // The queue row carries no MOID; the pending-detail row does. Remarks are keyed on chequeid.
    useEffect(() => {
        if (!cheque?.MOID || !roleId) return;
        if (!isReturned) dispatch(fetchStatusList({ MOID: cheque.MOID, ROID: roleId, ChkAmt: 0 }));
        dispatch(setSelectedMOID(cheque.MOID));
        dispatch(fetchRemarks({ trno: cheque.chequeid, moid: cheque.MOID }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cheque?.MOID, cheque?.chequeid]);

    // Prefill the resubmit form once the returned cheque book is in. Legacy leaves Remarks blank.
    useEffect(() => {
        if (!isReturned || !cheque) return;
        setUpd({
            bankId: cheque.BankId ? String(cheque.BankId) : '',
            date: legacyToIso(cheque.CreationDate),
            from: cheque.FirstChequeno || '',
            to: cheque.LastChequeno || '',
            remarks: '',
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cheque?.chequeid]);

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const handleRefresh = () => {
        if (roleId) dispatch(fetchVerifyChequeQueue(roleId));
    };

    const finishAndRefresh = () => {
        setTimeout(() => {
            dispatch(fetchVerifyChequeQueue(roleId));
            setSelectedItem(null);
            setComment('');
            setShowRemarksHistory(false);
            setIsLeftPanelCollapsed(false);
            dispatch(clearVerifyChequeDetail());
            dispatch(resetApprovalData());
        }, 800);
    };

    const handleActionClick = async (action) => {
        if (!cheque) { toast.error('No cheque book selected'); return; }
        if (!comment.trim()) { toast.error('Enter Note'); return; }

        const actionValue = action.type;
        // spVerifyChequeno reads @Action from Apprstatus.
        const payload = {
            chequeid: cheque.chequeid,
            Apprstatus: actionValue,
            ApprovalRemarks: comment.trim(),
            CreatedBy: getCurrentUser(),
            RoleID: roleId,
        };

        try {
            const result = await dispatch(submitVerifyCheque(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            if (status === 'Submitted' || status === 'Submited') {
                toast.success(`Cheque Book ${ACTION_VERB[actionValue] || `${actionValue}d`}`);
                finishAndRefresh();
                return;
            }
            toast.error(status || 'Error Occurred While Cheque Book Verification');
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred While Cheque Book Verification');
        }
    };

    const handleChequeNoChange = (key, value) => {
        if (value === '' || /^\d{0,6}$/.test(value)) setField(key, value);
    };

    const handleResubmit = async () => {
        if (!cheque) return;
        const errors = [];
        if (!upd.bankId) errors.push('Select Bank Name');
        if (!upd.date) errors.push('Select date');
        if (!upd.from) errors.push('Enter First Cheque Number');
        if (!upd.to) errors.push('Enter Last Cheque Number');
        // Legacy checked only the last number's length (twice); both must be 6 digits.
        if (upd.from.length !== 6 || upd.to.length !== 6) errors.push('Please enter six digits in From and To');
        if (upd.from && upd.to && Number(upd.from) >= Number(upd.to)) errors.push('Please enter valid cheque nos');
        if (!upd.remarks.trim()) errors.push('Please Enter Remarks');
        if (errors.length) { errors.forEach((m) => toast.error(m)); return; }

        // spChequebookReturn reads the bank id from BankName.
        const payload = {
            chequeid: cheque.chequeid,
            BankName: upd.bankId,
            ChqOpeningDate: isoToLegacy(upd.date),
            FirstChequeno: upd.from,
            LastChequeno: upd.to,
            Remarks: upd.remarks.trim(),
            CreatedBy: getCurrentUser(),
            RoleID: roleId,
        };

        try {
            const result = await dispatch(submitUpdateCheque(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            if (status === 'Successfull' || status === 'Submitted') {
                toast.success('Cheque Book Updated Successfully');
                finishAndRefresh();
                return;
            }
            toast.error(status || 'Error Occurred While Adding Cheque Nos.');
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred While Adding Cheque Nos.');
        }
    };

    const filteredQueue = queue.filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return item.BankName?.toLowerCase().includes(q)
            || item.CreationDate?.toLowerCase().includes(q)
            || item.Remarks?.toLowerCase().includes(q);
    });

    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 rounded-full border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">{item.BankName}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">Issued {item.CreationDate}</p>
                </div>
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                {item.Remarks && <p className="line-clamp-2">{item.Remarks}</p>}
                <div className="flex justify-end"><StatusBadge status={item.Approvalstatus} /></div>
            </div>
        </div>
    );

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="text-gray-500 dark:text-gray-400 min-w-[100px]">{item.CreationDate}</span>
            <span className="font-semibold text-gray-900 dark:text-white min-w-[200px] truncate">{item.BankName}</span>
            <span className="text-gray-500 dark:text-gray-400 truncate max-w-[320px]">{item.Remarks}</span>
            <span className="ml-auto"><StatusBadge status={item.Approvalstatus} /></span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    const renderPendingDetails = () => (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field label="Bank" value={cheque.BankName} />
                <Field label="Issue Date" value={cheque.CreationDate} />
                <Field label="Cheque No From" value={cheque.FirstChequeno} />
                <Field label="Cheque No To" value={cheque.LastChequeno} />
                <Field label="Remarks" value={cheque.Remarks} className="sm:col-span-2 lg:col-span-4" />
            </div>
        </div>
    );

    const renderResubmitForm = () => (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-5 border-2 border-amber-200 dark:border-amber-700 space-y-4">
            <div className="flex items-center gap-2">
                <RotateCcw className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                <h3 className="text-sm font-bold text-amber-800 dark:text-amber-300">Returned for Update — correct and resubmit</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className={labelCls}>Bank Name <span className="text-red-500">*</span></label>
                    <select className={inputCls} value={upd.bankId} onChange={(e) => setField('bankId', e.target.value)}>
                        <option value="">-Please Select-</option>
                        {banks.map((b) => <option key={b.Bank_Id} value={b.Bank_Id}>{b.Bank_Name}</option>)}
                    </select>
                </div>
                <div>
                    <label className={labelCls}>Issue Date <span className="text-red-500">*</span></label>
                    <input type="date" className={inputCls} value={upd.date} max={todayIso()} onChange={(e) => setField('date', e.target.value)} />
                </div>
                <div>
                    <label className={labelCls}>First Cheque No <span className="text-red-500">*</span></label>
                    <input type="text" inputMode="numeric" maxLength={6} className={inputCls} value={upd.from} onChange={(e) => handleChequeNoChange('from', e.target.value)} />
                </div>
                <div>
                    <label className={labelCls}>Last Cheque No <span className="text-red-500">*</span></label>
                    <input type="text" inputMode="numeric" maxLength={6} className={inputCls} value={upd.to} onChange={(e) => handleChequeNoChange('to', e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                    <label className={labelCls}>Remarks <span className="text-red-500">*</span></label>
                    <textarea rows={3} className={inputCls} value={upd.remarks} onChange={(e) => setField('remarks', e.target.value)} />
                </div>
            </div>
            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={handleResubmit}
                    disabled={updateLoading}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                    {updateLoading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Send className="w-4 h-4" />}
                    Resubmit
                </button>
            </div>
        </div>
    );

    const renderActions = () => {
        if (statusLoading) {
            return (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        <span className="text-gray-600 dark:text-gray-400">Loading actions…</span>
                    </div>
                </div>
            );
        }
        if (statusError) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ Error loading actions: {statusError}</p>
                </div>
            );
        }
        if (!hasActions || !enabledActions || enabledActions.length === 0) {
            return (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                    <p className="text-yellow-700 dark:text-yellow-400 text-center">ℹ️ No actions available for this cheque book</p>
                </div>
            );
        }
        return (
            <ActionButtons
                actions={enabledActions}
                onActionClick={handleActionClick}
                loading={verifyLoading}
                isVerified={true}
                comment={comment}
                showValidation={true}
            />
        );
    };

    const renderDetailContent = () => {
        if (!selectedItem) return null;

        if (detailLoading || (!cheque && !detailError)) {
            return (
                <div className="flex items-center justify-center gap-3 py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Loading cheque book details…</span>
                </div>
            );
        }
        if (detailError || !cheque) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ {detailError || 'Cheque book details could not be loaded — it may no longer be pending at your level.'}</p>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6 border-2 border-indigo-200 dark:border-indigo-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{cheque.BankName}</h2>
                    <p className="text-indigo-600 dark:text-indigo-400 font-semibold mb-3">
                        Cheques {cheque.FirstChequeno} – {cheque.LastChequeno}
                    </p>
                    <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium flex items-center gap-1">
                            <Building2 className="w-3 h-3" /> Issued {cheque.CreationDate}
                        </span>
                        <StatusBadge status={selectedItem.Approvalstatus} />
                    </div>
                </div>

                {isReturned ? null : renderPendingDetails()}

                <RemarksHistory
                    isOpen={showRemarksHistory}
                    onToggle={() => setShowRemarksHistory(!showRemarksHistory)}
                    remarks={remarks}
                    loading={remarksLoading}
                    title="Approval History"
                />

                {isReturned ? renderResubmitForm() : (
                    <>
                        <SimpleCommentInput
                            comment={comment}
                            onCommentChange={(e) => setComment(e.target.value)}
                            label="Notes"
                            placeholder="Enter verification notes…"
                            required
                            rows={3}
                            showCharCount
                        />
                        {renderActions()}
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || 'Cheque Book Verification'} (${queue.length})`}
                subtitle={ModuleDisplayName}
                itemCount={queue.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={BookOpen}
                badgeText="Cheque Book Verification"
                badgeCount={queue.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by bank, issue date, remarks…',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredQueue,
                    selectedItem,
                    onItemSelect: setSelectedItem,
                    renderItem: renderItemCard,
                    renderListItem,
                    renderCollapsedItem,
                    loading: queueLoading,
                    error: queueError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending',
                        icon: Clock,
                        emptyMessage: 'No cheque books pending verification!',
                        itemKey: 'chequeid',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                    },
                    renderPopupContent: () => renderDetailContent(),
                    popupConfig: {
                        title: 'Cheque Book Verification',
                        icon: BookOpen,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Cheque Book Details',
                        icon: BookOpen,
                        selectedTitle: 'Cheque Book Verification',
                        emptyTitle: 'No Cheque Book Selected',
                        emptyMessage: 'Select a cheque book from the list to view details and take action.',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxHeight: 'calc(100vh-200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />
        </div>
    );
};

export default VerifyCheque;
